import { PAYMENT_FORMS } from 'constants/paymentSettingShared.constants'

import type { Nullable } from 'types/helpers'
import type { DealPaymentMethodChoice } from 'shop/types/deal-payment-method-choice'

/** What the page renders when no contract payload reached it at all. */
export const EMPTY_DEAL_PAYMENT_METHOD_CHOICE: DealPaymentMethodChoice = {
  enabled: false,
  offeredMethods: [],
  recommendedMethod: null,
  unavailableReason: null,
  refusedMethods: [],
}

/**
 * `pay_later` is excluded from a deal's set by decision (spec §3.1): the backend
 * force-adds it to every sales-team plan for uniformity, so a literal reading of
 * R1 would hand every payer a pay-on-invoice option no seller knowingly chose.
 * The backend already subtracts it — this filter is the belt to that braces, so
 * a regression upstream cannot reach the payer.
 */
const isOfferable = (method: string): boolean => !!method && method !== PAYMENT_FORMS.payLater

/**
 * Normalises the contract payload into the shape the page renders.
 *
 * Two invariants it enforces, both load-bearing:
 *  - the recommended method sits at index 0, because both of `PurchaseMethods`'
 *    render branches treat index 0 as "the recommended one" (spec §3.1);
 *  - `recommendedMethod` is non-null only when it is actually offered, so
 *    R3-AC1's "nothing pre-selected" cannot be got wrong downstream.
 */
export const normalizeDealPaymentMethodChoice = (
  payload: Partial<DealPaymentMethodChoice> | null | undefined
): DealPaymentMethodChoice => {
  if (!payload) {
    return EMPTY_DEAL_PAYMENT_METHOD_CHOICE
  }

  const offered = (payload.offeredMethods || []).filter(isOfferable)
  const rawRecommended = payload.recommendedMethod
  const recommendedMethod = rawRecommended && offered.includes(rawRecommended) ? rawRecommended : null

  const offeredMethods = recommendedMethod
    ? [recommendedMethod, ...offered.filter((method) => method !== recommendedMethod)]
    : offered

  return {
    enabled: !!payload.enabled,
    offeredMethods,
    recommendedMethod,
    unavailableReason: payload.unavailableReason || null,
    refusedMethods: (payload.refusedMethods || []).filter((method) => offeredMethods.includes(method)),
  }
}

/**
 * Today's behaviour, for a seller the gate is off for (R5-AC1): the payer is
 * offered the one method their sales contact stored, and none of the states this
 * epic adds. Keeping the clamp here means `DealPaymentMethods` needs no second
 * code path to be correct — it renders one method because it is handed one.
 */
export const clampToRecommendedMethod = (
  choice: DealPaymentMethodChoice | null | undefined,
  storedMethod?: Nullable<string>
): DealPaymentMethodChoice => {
  /*
   * `storedMethod` is the deal's own `selectedPayMethod`, which is what the page
   * rendered before this epic and is on the deal payload whatever the backend
   * knows about the contract. Without it a deal page served by a backend that
   * sends no `payment_method_choice` — an older deploy, an error, any cause —
   * offers the payer nothing at all, which is worse than the behaviour this
   * clamp exists to preserve (R5-AC1).
   */
  const method = choice?.recommendedMethod || storedMethod

  if (!method || !isOfferable(method)) {
    return EMPTY_DEAL_PAYMENT_METHOD_CHOICE
  }

  return {
    enabled: false,
    offeredMethods: [method],
    recommendedMethod: method,
    unavailableReason: null,
    refusedMethods: [],
  }
}
